import { Static, Type } from '@sinclair/typebox';
import { THUMBOR_META_KEY, withThumborQuality } from './image';
import { ExternalUrlSchema, OpeningHoursSchema } from './professional';
import { PrestationVideoSchema } from './vod';
import { SeoScopeContextSchema } from './professional/seo-scope.schema';
import { ServiceAreasSchema } from './professional/service-area.schema';
import { PriceTypeSchema } from './translation_namespace/price_namespace';
import { PrestationTypeSchema } from './translation_namespace/prestation_namespace';
import { PlaceKind } from './translation_namespace/place_kind_namespace';
import { PublicPrestationPromotionSchema } from './promotion-public';
import { FaviconSizesSchema, WebsiteConfigSchema } from './website_types/WebsiteTypes';

// ─── Public Address (subset of AddressSchema) ───────────────────────

export const PublicAddressSchema = Type.Object({
  formatted_address: Type.String(),
  latitude: Type.Number(),
  longitude: Type.Number(),
});

// ─── Resized Image ──────────────────────────────────────────────────

// WIDTHx0 = width-constrained, aspect ratio preserved.
// Schéma des PHOTOS de contenu (établissement, prestation, article, avatar) : on
// force q76 (`withThumborQuality`) pour alléger le LCP — cf. `IMG_CONTENT_QUALITY`.
// ⚠️ NE PAS utiliser pour le logo : le schéma dédié `ResizedImageLogoSchema` conserve
// la qualité par défaut (bords/texte nets).
export const ResizedImageSchema = Type.Object(
  {
    small_url: Type.String({
      format: 'uri',
      meta: { [THUMBOR_META_KEY]: withThumborQuality('200x0') },
    }),
    medium_url: Type.String({
      format: 'uri',
      meta: { [THUMBOR_META_KEY]: withThumborQuality('600x0') },
    }),
    large_url: Type.String({
      format: 'uri',
      meta: { [THUMBOR_META_KEY]: withThumborQuality('1200x0') },
    }),
  },
  { meta: { picture: true } },
);

// Variante SANS filtre qualité, réservée au logo : un logo (texte, aplats, bords
// nets) se dégrade visiblement à q76 alors qu'une photo non. Même formes de champs
// que `ResizedImageSchema` pour rester interchangeable côté consommateurs.
export const ResizedImageLogoSchema = Type.Object(
  {
    small_url: Type.String({
      format: 'uri',
      meta: { [THUMBOR_META_KEY]: '200x0' },
    }),
    medium_url: Type.String({
      format: 'uri',
      meta: { [THUMBOR_META_KEY]: '600x0' },
    }),
    large_url: Type.String({
      format: 'uri',
      meta: { [THUMBOR_META_KEY]: '1200x0' },
    }),
  },
  { meta: { picture: true } },
);

// ─── Professional Profile ───────────────────────────────────────────

export const PublicProfessionalResponseSchema = Type.Object({
  slug: Type.String(),
  name: Type.String(),
  city: Type.Optional(Type.String()),
  address: Type.Optional(PublicAddressSchema),
  // TODO: fields below are not yet in DB
  jobs: Type.Optional(Type.Array(Type.String())),
  description: Type.Optional(Type.String()),
  logo: Type.Optional(ResizedImageLogoSchema),
  images: Type.Optional(Type.Array(ResizedImageSchema)),
  external_urls: Type.Optional(Type.Array(ExternalUrlSchema)),
  features: Type.Optional(
    Type.Object({
      can_book: Type.Boolean(),
      can_gift: Type.Boolean(),
      // Tunnel VTC actif : ride_config payant + au moins une prestation « Course » avec
      // flotte réservable (véhicule POOL actif et tarifé) — le tunnel est prestation-only.
      can_ride: Type.Optional(Type.Boolean()),
      fifty_taxe_credit: Type.Boolean(),
    }),
  ),
  allowed_payment_methods: Type.Optional(Type.Array(Type.String())),
  /* Default booking-tunnel appearance — used by /book/[slug] unless overridden via query params */
  tunnel_appearance: Type.Optional(
    Type.Object({
      accent: Type.Optional(Type.String()),
      background: Type.Optional(Type.String()),
      text: Type.Optional(Type.String()),
      radius: Type.Optional(Type.Number()),
    }),
  ),
  /* Public booking-tunnel behavior flags (subset of pro_booking_params) */
  booking_params: Type.Optional(
    Type.Object({
      allow_multi_prestation: Type.Optional(Type.Boolean()),
      allow_multi_member: Type.Optional(Type.Boolean()),
    }),
  ),
  /**
   * Semi-gel dunning (impayé J+30) ou abo gelé : la réservation en ligne est coupée. Les
   * tunnels (widget site custom, /book, widget embarqué) affichent une carte « appelez-nous »
   * à la place — JAMAIS la raison. Le refus dur est côté API.
   *
   * Le numéro n'est volontairement PAS servi ici : `pro:phone` est un binding protégé
   * (masqué en SSR, révélé via l'endpoint captcha `reveal-bindings`). L'exposer en clair
   * dans un profil public annulerait cette protection anti-scraping — la carte le récupère
   * à la demande, au clic, par le même chemin que ContactReveal.
   */
  online_booking_suspended: Type.Optional(Type.Boolean()),
  /**
   * Option marque blanche active (jamais en essai, cf. resolveWhiteLabelEntitlement) :
   * les pages publiques (/book, /gift-card, /manage) masquent le « Propulsé par ».
   */
  is_white_label: Type.Optional(Type.Boolean()),
});
export type PublicProfessionalResponseObject = Static<typeof PublicProfessionalResponseSchema>;

// ─── Price Option (for booking tunnel) ─────────────────────────────

export const PublicPriceOptionSchema = Type.Object({
  // Identifiant stable de la formule : le panier du site et le tunnel la désignent par lui.
  price_id: Type.Optional(Type.Number()),
  duration_minutes: Type.Number(),
  price: Type.Number(),
  price_type: Type.String(),
  price_string: Type.Optional(Type.String({ meta: { is_price: true } })),
  label: Type.Optional(Type.String()),
});
export type PublicPriceOptionObject = Static<typeof PublicPriceOptionSchema>;

// ─── Prestation Supplement ─────────────────────────────────────────

export const PublicPrestationSupplementSchema = Type.Object({
  supplement_id: Type.Number(),
  name: Type.String(),
  description: Type.Optional(Type.String()),
  price: Type.Optional(Type.Number()),
  price_string: Type.Optional(Type.String({ meta: { is_price: true } })),
  duration_minutes: Type.Optional(Type.Number()),
  /** Le client choisit combien il en prend (ni durée ni salle, cf. `isSupplementQuantitySelectable`). */
  quantity_selectable: Type.Optional(Type.Boolean()),
  /**
   * Tunnel : lieux où ce supplément est proposé (sa salle s'y trouve, cf. room-place-rule). Absent :
   * proposé partout. Même convention que `pro_place_ids` d'une prestation (intersection du panier).
   */
  pro_place_ids: Type.Optional(Type.Array(Type.Number())),
});
export type PublicPrestationSupplementObject = Static<typeof PublicPrestationSupplementSchema>;

// ─── Pack containing the prestation (gift/forfait CTA) ──────────────

// Une prestation incluse dans un forfait (contenu affiché dans la modale « ce forfait comprend »).
export const PublicPackContentSchema = Type.Object({
  name: Type.String(),
  quantity: Type.Number(),
});
export type PublicPackContentObject = Static<typeof PublicPackContentSchema>;

// Pack actif offrable contenant la prestation : CTA « forfait » près d'« Offrir » → modale contenu
// (nom, prix, description, prestations incluses) → « Acheter » deep-linke le tunnel (`?pack=<id>`).
export const PublicPrestationPackSchema = Type.Object({
  pack_id: Type.Number(),
  name: Type.String(),
  description: Type.Optional(Type.String()),
  price_string: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'price' } })),
  // Visuels du forfait (mêmes que le bloc « Forfaits ») → affichés dans la modale « contenu du forfait ».
  pictures: Type.Optional(Type.Array(ResizedImageSchema)),
  prestations: Type.Optional(Type.Array(PublicPackContentSchema)),
});
export type PublicPrestationPackObject = Static<typeof PublicPrestationPackSchema>;

// Pack actif achetable (forfait) pour le composant builder « Forfaits » : carte avec nom, description,
// prix et visuel + modale contenu (prestations incluses) + « Acheter » → tunnel `?pack=<id>`. Exposé
// seulement si offrable (allow_packs).
export const PublicPackItemSchema = Type.Object({
  pack_id: Type.Number(),
  name: Type.String(),
  description: Type.Optional(Type.String()),
  price_string: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'price' } })),
  pictures: Type.Optional(Type.Array(ResizedImageSchema)),
  prestations: Type.Optional(Type.Array(PublicPackContentSchema)),
});
export type PublicPackItemObject = Static<typeof PublicPackItemSchema>;

export const PublicPacksResponseSchema = Type.Object({
  packs: Type.Array(PublicPackItemSchema),
});
export type PublicPacksResponseObject = Static<typeof PublicPacksResponseSchema>;

// ─── Prestation Item (list view) ────────────────────────────────────

export const PublicPrestationItemSchema = Type.Object({
  prestation_id: Type.Number(),
  name: Type.String(),
  slug: Type.String(),
  description: Type.Optional(Type.String()),
  color: Type.Optional(Type.String()),
  // Type de prestation (STANDARD | RIDE). Pilote le rendu catalogue : une prestation RIDE
  // (« Course ») n'a ni prix ni durée — la carte affiche à la place un badge « Course » + la
  // capacité du pool (ci-dessous), et son CTA mène au tunnel ride au lieu du tunnel classique.
  type: Type.Optional(PrestationTypeSchema()),
  // Capacité agrégée du pool de véhicules réservables — items RIDE UNIQUEMENT. Mêmes règles
  // que RidePublicPrestationSchema : min des mins / max des maxes des bornes non-nulles ; une
  // borne est ABSENTE dès qu'un véhicule du pool est sans contrainte sur cet axe.
  pax_min: Type.Optional(Type.Number()),
  pax_max: Type.Optional(Type.Number()),
  small_luggage_min: Type.Optional(Type.Number()),
  small_luggage_max: Type.Optional(Type.Number()),
  large_luggage_min: Type.Optional(Type.Number()),
  large_luggage_max: Type.Optional(Type.Number()),
  pictures: Type.Optional(Type.Array(ResizedImageSchema)),
  videos: Type.Optional(Type.Array(PrestationVideoSchema)),
  category_id: Type.Optional(Type.Number()),
  category_name: Type.Optional(Type.String()),
  category_slug: Type.Optional(Type.String()),
  price: Type.Optional(Type.Number()),
  price_string: Type.Optional(Type.String({ meta: { is_price: true } })),
  price_type: Type.Optional(PriceTypeSchema()),
  prices: Type.Optional(Type.Array(PublicPriceOptionSchema)),
  // Devise du pro (code ISO 4217, casse non garantie : cf. `resolveSiteEventCurrency`) : celle
  // des prix bruts ci-dessus, reprise par l'événement de suivi `add_to_cart`.
  currency: Type.Optional(Type.String()),
  supplements: Type.Optional(Type.Array(PublicPrestationSupplementSchema)),
  max_quantity: Type.Optional(Type.Number()),
  /* Taille de groupe : le visiteur choisit COMBIEN DE PERSONNES la réservation embarque
     (`party_size`), et le prix suit ce nombre (cf. computeBillableUnits). Exposé au site public
     parce que la fiche prestation doit poser la question AVANT l'ajout au panier : quand le
     tunnel s'hydrate depuis ce panier il SAUTE l'étape prestations, donc son propre sélecteur
     n'apparaîtrait jamais. Les deux plafonds bornent la saisie (cf. resolvePartySizeMax). */
  is_party_size_open: Type.Optional(Type.Boolean()),
  clients_per_booking_max: Type.Optional(Type.Number()),
  max_session_capacity: Type.Optional(Type.Number()),
  // Vrai ⟺ la prestation est offrable INDIVIDUELLEMENT (carte cadeau configurée pour elle :
  // allow_prestations + sélection directe/catégorie + variante FIXE > 0). Pilote le CTA « Offrir »
  // (qui ouvre le tunnel en présélectionnant CETTE prestation). L'offrabilité via pack est portée
  // séparément par `packs` (CTA forfait dédié) — cf. backend computeGiftablePrestationIds.
  is_giftable: Type.Optional(Type.Boolean()),
  // Packs actifs offrables contenant la prestation (détail uniquement) → CTA « forfait » dédié.
  packs: Type.Optional(Type.Array(PublicPrestationPackSchema)),
  // Vrai ⟺ le pro a AU MOINS un forfait achetable (indépendant de cette prestation) → pilote le CTA
  // global « Nos forfaits » près d'« Offrir » (ouvre le tunnel forfaits, choix du forfait). Détail uniquement.
  has_packs: Type.Optional(Type.Boolean()),
  // Promotion mise en avant qui vise la prestation (badge, prix barré si acquise, bloc Offre en cours).
  promotion: Type.Optional(PublicPrestationPromotionSchema),
});
export type PublicPrestationItemObject = Static<typeof PublicPrestationItemSchema>;

// ─── Prestation Detail (extends Item with no extra fields for now) ──

export const PublicPrestationDetailSchema = Type.Object({
  ...PublicPrestationItemSchema.properties,
});
export type PublicPrestationDetailObject = Static<typeof PublicPrestationDetailSchema>;

// ─── Category ───────────────────────────────────────────────────────

export const PublicCategorySchema = Type.Object({
  catalog_category_id: Type.Number(),
  name: Type.String(),
  slug: Type.String(),
  description: Type.Optional(Type.String()),
  // Comme `PublicPrestationItemSchema.pictures` : objets `ResizedImageSchema`
  // (meta.picture) signés par `transformToSchema`. Un `string[]` nu n'est jamais
  // signé → la clé S3 brute fuit dans le rendu public (carré blanc).
  pictures: Type.Optional(Type.Array(ResizedImageSchema)),
});
export type PublicCategoryObject = Static<typeof PublicCategorySchema>;

// ─── Prestations Response (combined categories + prestations) ───────

export const PublicPrestationsResponseSchema = Type.Object({
  categories: Type.Array(PublicCategorySchema),
  prestations: Type.Array(PublicPrestationItemSchema),
});
export type PublicPrestationsResponseObject = Static<typeof PublicPrestationsResponseSchema>;

// ─── Prestations Highlight Response ──────────────────────────────────

export const PublicPrestationsHighlightResponseSchema = Type.Object({
  prestations: Type.Array(PublicPrestationItemSchema),
});
export type PublicPrestationsHighlightResponseObject = Static<typeof PublicPrestationsHighlightResponseSchema>;

// ─── Team Member ────────────────────────────────────────────────────

export const PublicTeamMemberSchema = Type.Object({
  team_member_id: Type.Number(),
  firstname: Type.Optional(Type.String()),
  lastname: Type.Optional(Type.String()),
  description: Type.Optional(Type.String()),
  picture: Type.Optional(Type.Array(ResizedImageSchema)),
  expertise_domains: Type.Optional(Type.Array(Type.String())),
  career_path: Type.Optional(
    Type.Array(
      Type.Object({
        name: Type.String(),
        start_year: Type.Number(),
        end_year: Type.Optional(Type.Number()),
      }),
    ),
  ),
});
export type PublicTeamMemberObject = Static<typeof PublicTeamMemberSchema>;

// ─── Place ──────────────────────────────────────────────────────────

// Sous-ensemble whitelist de `type_config` : on n'expose JAMAIS les données sensibles
// (video_link, phone_number) côté API publique. Ces champs sont diffusés uniquement
// dans les notifications post-booking (cf. binding-resolver.ts).
const PublicPlaceTypeConfigSchema = Type.Object({
  instructions: Type.Optional(Type.String()),
  travel_radius_km: Type.Optional(Type.Integer()),
  provider: Type.Optional(Type.String()),
});

export const PublicPlaceSchema = Type.Object({
  pro_place_id: Type.Number(),
  label: Type.String(),
  kind: Type.Enum(PlaceKind),
  address: Type.Optional(PublicAddressSchema),
  type_config: Type.Optional(PublicPlaceTypeConfigSchema),
  opening_hours: Type.Optional(OpeningHoursSchema),
  // TODO: google_reviews not yet available (no score/ratings in google_location)
  google_reviews: Type.Optional(
    Type.Object({
      rating: Type.Number(),
      total_reviews: Type.Number(),
    }),
  ),
});
export type PublicPlaceObject = Static<typeof PublicPlaceSchema>;

// ─── Contact Details (captcha-protected) ─────────────────────────────

export const PublicContactDetailsSchema = Type.Object({
  phone: Type.Optional(Type.String()),
  email: Type.Optional(Type.String()),
});
export type PublicContactDetailsObject = Static<typeof PublicContactDetailsSchema>;

// ─── Reveal Bindings (captcha-protected) ─────────────────────────────

export const RevealBindingsRequestSchema = Type.Object({
  keys: Type.Array(Type.String({ pattern: '^[a-z0-9_:-]+$' }), { minItems: 1, maxItems: 10 }),
});
export type RevealBindingsRequestObject = Static<typeof RevealBindingsRequestSchema>;

export const RevealBindingsResponseSchema = Type.Object({
  values: Type.Record(Type.String(), Type.String()),
});
export type RevealBindingsResponseObject = Static<typeof RevealBindingsResponseSchema>;

// ─── Contact / Callback Submission (captcha-protected) ───────────────
// Soumission publique des blocs « Formulaire de contact » (CONTACT) et
// « Demande de rappel » (CALLBACK). Les champs étant définis par le pro dans le
// builder, on ne valide ici que la *forme* : un tableau borné de réponses libres.

export enum WebsiteContactSubmissionKind {
  CONTACT = 'CONTACT',
  CALLBACK = 'CALLBACK',
}

export const ContactSubmissionAnswerSchema = Type.Object({
  // Id du champ tel que défini dans le builder (WebsiteFormField.id).
  key: Type.String({ minLength: 1, maxLength: 64 }),
  label: Type.String({ minLength: 1, maxLength: 255 }),
  type: Type.String({ maxLength: 32 }),
  value: Type.String({ maxLength: 5000 }),
});
export type ContactSubmissionAnswerObject = Static<typeof ContactSubmissionAnswerSchema>;

export const ContactSubmissionRequestSchema = Type.Object({
  kind: Type.Enum(WebsiteContactSubmissionKind),
  answers: Type.Array(ContactSubmissionAnswerSchema, { minItems: 1, maxItems: 30 }),
  consent: Type.Boolean(),
  // URL de la page d'où la soumission provient (pour contexte côté pro).
  source_url: Type.Optional(Type.String({ maxLength: 2000 })),
});
export type ContactSubmissionRequestObject = Static<typeof ContactSubmissionRequestSchema>;

export const ContactSubmissionResponseSchema = Type.Object({
  success: Type.Boolean(),
  // Identifiant public de la demande (`website_contact_submission.public_id`, uuid), repris par
  // l'événement de suivi `generate_lead`. L'id auto-incrémenté n'est jamais exposé.
  lead_id: Type.String(),
  // Email et téléphone RETENUS pour la demande (les mêmes que sur la fiche du lead), téléphone en
  // E.164 lu avec le pays du pro, absent s'il n'est pas valide. Repris tels quels dans le
  // `user_data` de `generate_lead` : le front ne re-choisit pas un champ ni ne devine un indicatif.
  user_data: Type.Optional(
    Type.Object({
      email: Type.Optional(Type.String()),
      phone_number: Type.Optional(Type.String()),
    }),
  ),
});
export type ContactSubmissionResponseObject = Static<typeof ContactSubmissionResponseSchema>;

// ─── Legal ──────────────────────────────────────────────────────────

export const PublicLegalSchema = Type.Object({
  legal_name: Type.Optional(Type.String()),
  siret: Type.Optional(Type.String()),
});
export type PublicLegalObject = Static<typeof PublicLegalSchema>;

// ─── SEO Globals ────────────────────────────────────────────────────

export const SeoAddressSchema = Type.Object({
  formatted_address: Type.Optional(Type.String()),
  street_address: Type.Optional(Type.String()),
  city: Type.Optional(Type.String()),
  region: Type.Optional(Type.String()),
  zip: Type.Optional(Type.String()),
  country: Type.Optional(Type.String()),
  latitude: Type.Optional(Type.Number()),
  longitude: Type.Optional(Type.Number()),
});
export type SeoAddressObject = Static<typeof SeoAddressSchema>;

export const SeoGlobalsSchema = Type.Object({
  name: Type.String(),
  description: Type.Optional(Type.String()),
  logo_url: Type.Optional(Type.String({ format: 'uri' })),
  /**
   * Icônes carrées dérivées du logo (mêmes tailles qu'un favicon configuré), servies
   * comme favicon de repli quand le pro n'a pas renseigné de favicon dans le builder.
   * Distinct de `logo_url`, qui est le logo en `600x0` destiné à la carte OG.
   */
  logo_icon_sizes: Type.Optional(FaviconSizesSchema),
  address: Type.Optional(SeoAddressSchema),
  phone: Type.Optional(Type.String()),
  email: Type.Optional(Type.String()),
  external_urls: Type.Optional(Type.Array(ExternalUrlSchema)),
  siret: Type.Optional(Type.String()),
  legal_name: Type.Optional(Type.String()),
  // Périmètre géographique SEO du pro (LOCAL/NATIONAL) + données dérivées
  // pour les templates conditionnels (primary_area, areaServed, etc.).
  // Produit par `buildSeoScopeContext()` côté backend, consommé par les
  // templates `<title>` / meta / JSON-LD de custom-websites.
  scope_context: Type.Optional(SeoScopeContextSchema),
});
export type SeoGlobalsObject = Static<typeof SeoGlobalsSchema>;

// ─── Website Config ─────────────────────────────────────────────────

/**
 * Règle de redirection servie au SSR : `source` est le chemin entrant
 * (commence par `/`), `destination` est déjà résolu (chemin interne absolu
 * ou URL externe), `permanent` pilote le code HTTP (308 vs 307).
 */
export const PublicRedirectRuleSchema = Type.Object({
  source: Type.String(),
  destination: Type.String(),
  permanent: Type.Boolean(),
});
export type PublicRedirectRule = Static<typeof PublicRedirectRuleSchema>;

/**
 * Script custom actif servi au SSR : champs minimaux (pas de label ni enabled —
 * seules les entrées `enabled` sont servies, et uniquement si le plan COURANT
 * du pro conserve le scope `website.scripts`).
 * Clés camelCase volontaires : miroir de `ScriptEntry` (blob WebsiteTypes).
 */
/* eslint-disable typebox-snake-case/typebox-object-keys-snake-case -- camelCase keys mirror the ScriptEntry blob schema */
export const PublicScriptEntrySchema = Type.Object({
  id: Type.String(),
  kind: Type.Union([Type.Literal('external'), Type.Literal('inline')]),
  src: Type.Optional(Type.String()),
  snippet: Type.Optional(Type.String()),
  position: Type.Union([Type.Literal('head'), Type.Literal('body_end')]),
  consentCategory: Type.Union([Type.Literal('necessary'), Type.Literal('analytics'), Type.Literal('marketing')]),
  async: Type.Optional(Type.Boolean()),
  defer: Type.Optional(Type.Boolean()),
});
/* eslint-enable typebox-snake-case/typebox-object-keys-snake-case */
export type PublicScriptEntry = Static<typeof PublicScriptEntrySchema>;

export const PublicWebsiteConfigResponseSchema = Type.Object({
  config: Type.Optional(WebsiteConfigSchema),
  domain: Type.Optional(Type.String()),
  is_published: Type.Boolean(),
  professional_slug: Type.Optional(Type.String()),
  professional_id: Type.Optional(Type.Number()),
  seo_globals: Type.Optional(SeoGlobalsSchema),
  /**
   * Zones d'intervention complètes du pro (cap 10), consommées par la page
   * mère `/lieux` + sa résolution `/lieux/[url_slug]` côté custom-websites,
   * et par le sitemap. `seo_globals.scope_context.service_areas_top3` ne
   * contient que les 3 noms ; ici on a la liste complète avec `area_type`
   * et `url_slug`.
   */
  service_areas: Type.Optional(ServiceAreasSchema),
  last_published_at: Type.Optional(Type.String({ format: 'date-time' })),
  /**
   * Règles de redirection actives, avec destination déjà résolue côté backend
   * (binding interne `{{prestation:45}}` → chemin `/prestations/...`, ou URL
   * externe telle quelle). Appliquées en SSR dans custom-websites avant le
   * matching de page. Seules les règles `enabled` sont servies.
   */
  redirects: Type.Optional(Type.Array(PublicRedirectRuleSchema)),
  /**
   * Scripts custom actifs (plan MAX). Entitlement re-vérifié à la livraison :
   * un downgrade coupe le rendu au prochain revalidate (~10 s) côté renderer.
   * Jamais renseigné sur le chemin lead-preview.
   */
  custom_scripts: Type.Optional(Type.Array(PublicScriptEntrySchema)),
  /**
   * Option marque blanche active (jamais en essai, cf. resolveWhiteLabelEntitlement) :
   * le footer du site masque le bloc « Propulsé par ». Entitlement re-vérifié à la
   * livraison (même mécanique que custom_scripts). Absent sur le chemin lead-preview
   * → défaut `false`, branding affiché.
   */
  is_white_label: Type.Optional(Type.Boolean()),
  /**
   * Empreinte des promotions mises en avant et ouvertes (identifiant et dernière modification) : change
   * quand une promotion s'ouvre, se termine, passe en pause ou est modifiée. Le site l'ajoute aux URL des
   * données préchargées pour ne pas servir un prix barré périmé depuis son cache.
   */
  catalog_version: Type.Optional(Type.String()),
});
export type PublicWebsiteConfigResponseObject = Static<typeof PublicWebsiteConfigResponseSchema>;

// ─── Newsletter Subscribe ──────────────────────────────────────────

export const PublicNewsletterSubscribeRequestSchema = Type.Object({
  email: Type.String({ format: 'email' }),
  instance_id: Type.String(),
});
export type PublicNewsletterSubscribeRequestObject = Static<typeof PublicNewsletterSubscribeRequestSchema>;

export const PublicNewsletterSubscribeResponseSchema = Type.Object({
  message: Type.String(),
});
export type PublicNewsletterSubscribeResponseObject = Static<typeof PublicNewsletterSubscribeResponseSchema>;

// ─── Migration v1 → v2 : URLs canoniques par id pro ─────────────────
// Résout l'id v2 (immuable) d'un pro vers son slug public courant + domaine
// perso en ligne. Consommé par les résolveurs de redirection `…/by-v1/{id}`
// (le front v1 ne stocke que `v2_pro_id` immuable ; slug/domaine — mutables —
// sont résolus ici à la volée). `custom_domain` n'est renseigné que si le
// domaine est réellement en ligne (cert HTTPS émis).
export const ProCanonicalResponseSchema = Type.Object({
  slug: Type.String(),
  custom_domain: Type.Union([Type.String(), Type.Null()]),
});
export type ProCanonicalResponseObject = Static<typeof ProCanonicalResponseSchema>;
